import { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Button, TextField, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import supabase from '../../../supabaseClient';
import AddOrders from './AddOrder';
import { formatCurrency } from './currency';

const statusOptions = [
    { value: 'nowe', label: 'Nowe', color: '#3D6E9E', bg: '#EDF2F7' },
    { value: 'w_realizacji', label: 'W realizacji', color: '#C97A22', bg: '#F7EEE3' },
    { value: 'zrealizowane', label: 'Zrealizowane', color: '#2E8552', bg: '#E8F1EC' },
];

const stanStyles = {
    'OK': { bgcolor: '#E4F3E9', color: '#2E8552' },
    'Brak na stanie': { bgcolor: '#FBE6E6', color: '#C7433F' },
};

// Funkcja pomocnicza do obliczania tygodnia ISO roku
const getWeekNumber = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

function OrdersDetails({ open, orderId, onClose, onStatusChanged }) {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Edycja wartości zamówienia
    const [editingValue, setEditingValue] = useState(false);
    const [valueDraft, setValueDraft] = useState('');
    const [savingValue, setSavingValue] = useState(false);

    // Edycja planowanej daty dostawy
    const [editingPlanningDate, setEditingPlanningDate] = useState(false);
    const [planningDateDraft, setPlanningDateDraft] = useState('');
    const [savingPlanningDate, setSavingPlanningDate] = useState(false);

    const [errorMsg, setErrorMsg] = useState('');
    const [idConfiguration, setIdConfiguration] = useState(null);

    // Pobranie id_configuration zalogowanego użytkownika z jego profilu
    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    async function fetchOrder() {
        setLoading(true);
        setErrorMsg('');

        const { data, error } = await supabase
            .from('orders')
            .select(`
                id,
                order_date,
                planning_order_date,
                realizacja,
                note,
                total_price,
                currency,
                kontrahenci ( name ),
                order_items ( id, product, amount, unit_price, product_id, products ( stock_quantity, min_price ) )
            `)
            .eq('id', orderId)
            .single();

        if (error) {
            console.error('Błąd pobierania szczegółów zamówienia:', error);
            setLoading(false);
            return;
        }

        setOrder(data);
        setLoading(false);
    }

    useEffect(() => {
        if (!open || !orderId) return;
        fetchOrder();
    }, [open, orderId]);

    const handleStatusChange = async (newStatus) => {
        if (!order || newStatus === order.realizacja) return;

        setUpdatingStatus(true);

        const { error } = await supabase
            .from('orders')
            .update({ realizacja: newStatus })
            .eq('id', order.id);

        if (error) {
            console.error('Błąd aktualizacji statusu:', error);
            setUpdatingStatus(false);
            return;
        }

        if (newStatus === 'zrealizowane' && idConfiguration) {
            for (const item of order.order_items) {
                if (!item.product_id) continue;

                const { error: transactionError } = await supabase.from('inventory_transactions').insert({
                    product_id: item.product_id,
                    transaction_type: 'wydanie',
                    quantity: item.amount,
                    transaction_date: new Date().toISOString().slice(0, 10),
                    document: `Wydanie pod zamówienie #${order.id.slice(0, 8)}`,
                    note: order.kontrahenci?.name ? `Zamówienie dla: ${order.kontrahenci.name}` : null,
                    id_configuration: idConfiguration,
                });

                if (transactionError) {
                    console.error('Błąd rejestracji wydania magazynowego:', transactionError);
                    continue;
                }

                const currentStock = Number(item.products?.stock_quantity ?? 0);
                const { error: stockError } = await supabase
                    .from('products')
                    .update({ stock_quantity: currentStock - Number(item.amount) })
                    .eq('id', item.product_id);

                if (stockError) {
                    console.error('Błąd aktualizacji stanu magazynowego:', stockError);
                }
            }
        }

        setUpdatingStatus(false);
        setOrder((prev) => ({ ...prev, realizacja: newStatus }));
        onStatusChanged?.();
    };

    const handleDelete = async () => {
        if (!order) return;

        setDeleting(true);
        const { error } = await supabase.from('orders').delete().eq('id', order.id);
        setDeleting(false);

        if (error) {
            console.error('Błąd usuwania zamówienia:', error);
            return;
        }

        setDeleteConfirmOpen(false);
        onStatusChanged?.();
        onClose();
    };

    const handleSaveValue = async () => {
        if (valueDraft === '' || Number(valueDraft) < 0) return;

        const newTotal = Number(valueDraft);

        for (const item of order.order_items) {
            const minPrice = item.products?.min_price;
            if (minPrice !== null && minPrice !== undefined) {
                const impliedUnitPrice = newTotal / order.order_items.reduce((acc, curr) => acc + Number(curr.amount), 0);
                if (impliedUnitPrice < Number(minPrice)) {
                    setErrorMsg(`Wartość zamówienia skutkuje ceną poniżej ceny minimalnej dla produktu "${item.product}" (${formatCurrency(minPrice, order.currency)}).`);
                    return;
                }
            }
        }

        setSavingValue(true);
        setErrorMsg('');

        const { error } = await supabase
            .from('orders')
            .update({ total_price: newTotal })
            .eq('id', order.id);

        setSavingValue(false);

        if (error) {
            console.error('Błąd zapisu wartości zamówienia:', error);
            setErrorMsg('Nie udało się zapisać wartości zamówienia.');
            return;
        }

        setOrder((prev) => ({ ...prev, total_price: newTotal }));
        setEditingValue(false);
        onStatusChanged?.();
    };

    const handleSavePlanningDate = async () => {
        setSavingPlanningDate(true);
        setErrorMsg('');

        const newDate = planningDateDraft ? planningDateDraft : null;

        const { error } = await supabase
            .from('orders')
            .update({ planning_order_date: newDate })
            .eq('id', order.id);

        setSavingPlanningDate(false);

        if (error) {
            console.error('Błąd zapisu planowanej daty dostawy:', error);
            setErrorMsg('Nie udało się zapisać planowanej daty dostawy.');
            return;
        }

        setOrder((prev) => ({ ...prev, planning_order_date: newDate }));
        setEditingPlanningDate(false);
        onStatusChanged?.();
    };

    if (!loading && !order) return null;

    const currentStatus = statusOptions.find((s) => s.value === order?.realizacja);
    const canEditOrDelete = order && order.realizacja !== 'zrealizowane';
    const weekNum = getWeekNumber(order?.planning_order_date);
    const draftWeekNum = getWeekNumber(planningDateDraft);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            <Box sx={{ bgcolor: currentStatus?.bg ?? '#EDF2F7', px: 4, pt: 3, pb: 3, transition: 'background-color 0.2s ease' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: 'text.secondary', mb: 0.5 }}>
                    STATUS REALIZACJI
                </Typography>
                <Typography sx={{ fontSize: '26px', fontWeight: 700, color: currentStatus?.color ?? 'text.primary', mb: 2 }}>
                    {currentStatus?.label ?? '—'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {statusOptions.map((status) => {
                        const isActive = order?.realizacja === status.value;
                        return (
                            <Button
                                key={status.value}
                                onClick={() => handleStatusChange(status.value)}
                                disabled={updatingStatus}
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    py: 1.2,
                                    bgcolor: isActive ? status.color : 'background.paper',
                                    color: isActive ? '#fff' : status.color,
                                    border: isActive ? 'none' : `1px solid ${status.color}33`,
                                    '&:hover': {
                                        bgcolor: isActive ? status.color : 'background.paper',
                                        opacity: isActive ? 0.9 : 1,
                                    },
                                }}
                            >
                                {status.label}
                            </Button>
                        );
                    })}
                </Box>
            </Box>

            <DialogContent sx={{ p: 4 }}>
                {loading || !order ? (
                    <Typography sx={{ color: 'text.secondary' }}>Ładowanie...</Typography>
                ) : (
                    <>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Kontrahent</Typography>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, mb: 3 }}>
                            {order.kontrahenci?.name ?? '—'}
                        </Typography>

                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>Pozycje zamówienia</Typography>
                        <Box sx={{ mb: 3 }}>
                            {order.order_items.map((item) => {
                                const dostepne = item.products?.stock_quantity ?? null;
                                const stan = dostepne === null ? null : item.amount > dostepne ? 'Brak na stanie' : 'OK';

                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            border: '1px solid #ECE7DE',
                                            borderRadius: 2,
                                            px: 2.5,
                                            py: 1.5,
                                            mb: 1,
                                            gap: 2,
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 700, flex: '0 0 180px', minWidth: 0 }}>
                                            {item.product}
                                        </Typography>

                                        <Box sx={{ flex: '0 0 110px' }}>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                Zamówiono
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{item.amount}</Typography>
                                        </Box>

                                        <Box sx={{ flex: '0 0 110px' }}>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                Dostępne
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{dostepne ?? '—'}</Typography>
                                        </Box>

                                        <Box sx={{ flex: '0 0 100px' }}>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                Cena
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{formatCurrency(item.unit_price, order.currency)}</Typography>
                                        </Box>

                                        <Box sx={{ flex: '1 1 auto' }}>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                Wartość
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600 }}>
                                                {formatCurrency(item.amount * item.unit_price, order.currency)}
                                            </Typography>
                                        </Box>

                                        {stan && (
                                            <Box
                                                sx={{
                                                    ...stanStyles[stan],
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    borderRadius: '8px',
                                                    px: 1.2,
                                                    py: 0.4,
                                                    flex: '0 0 auto',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {stan}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 4, mb: 3, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Data zamówienia</Typography>
                                <Typography sx={{ fontWeight: 600 }}>{order.order_date}</Typography>
                            </Box>

                            {/* Planowana data dostawy */}
                            <Box>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Planowana data dostawy</Typography>
                                {editingPlanningDate ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TextField
                                            size="small"
                                            type="date"
                                            value={planningDateDraft}
                                            onChange={(e) => setPlanningDateDraft(e.target.value)}
                                            sx={{ width: 160 }}
                                            SlotProps={{ inputLabel: { shrink: true } }}
                                        />
                                        {draftWeekNum && (
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'primary.main', ml: 0.5 }}>
                                                (Tydzień {draftWeekNum})
                                            </Typography>
                                        )}
                                        <IconButton size="small" onClick={handleSavePlanningDate} disabled={savingPlanningDate}>
                                            <CheckIcon fontSize="small" color="success" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => { setEditingPlanningDate(false); setErrorMsg(''); }} disabled={savingPlanningDate}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {order.planning_order_date
                                                ? `${order.planning_order_date} (Tydzień ${weekNum})`
                                                : '—'}
                                        </Typography>
                                        {canEditOrDelete && (
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setPlanningDateDraft(order.planning_order_date ?? '');
                                                    setEditingPlanningDate(true);
                                                    setErrorMsg('');
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            <Box>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Wartość zamówienia</Typography>
                                {editingValue ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            autoFocus
                                            value={valueDraft}
                                            onChange={(e) => setValueDraft(e.target.value)}
                                            sx={{ width: 130 }}
                                        />
                                        <IconButton size="small" onClick={handleSaveValue} disabled={savingValue}>
                                            <CheckIcon fontSize="small" color="success" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => { setEditingValue(false); setErrorMsg(''); }} disabled={savingValue}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ fontWeight: 700 }}>{formatCurrency(order.total_price ?? 0, order.currency)}</Typography>
                                        {canEditOrDelete && (
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setValueDraft(String(order.total_price ?? 0));
                                                    setEditingValue(true);
                                                    setErrorMsg('');
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {errorMsg && (
                            <Typography sx={{ color: '#C7433F', fontSize: '13px', mb: 2 }}>{errorMsg}</Typography>
                        )}

                        {order.note && (
                            <>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Uwagi</Typography>
                                <Box sx={{ bgcolor: '#FAF7F2', borderRadius: 2, px: 2, py: 1.5, mb: 3 }}>
                                    <Typography>{order.note}</Typography>
                                </Box>
                            </>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                            {canEditOrDelete && (
                                <>
                                    <Button
                                        startIcon={<DeleteIcon fontSize="small" />}
                                        onClick={() => setDeleteConfirmOpen(true)}
                                        color="error"
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, mr: 'auto' }}
                                    >
                                        Usuń
                                    </Button>
                                    <Button
                                        startIcon={<EditIcon fontSize="small" />}
                                        variant="outlined"
                                        onClick={() => setEditModalOpen(true)}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                                    >
                                        Edytuj
                                    </Button>
                                </>
                            )}
                            <Button
                                variant="contained"
                                onClick={onClose}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3 }}
                            >
                                Zamknij
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>

            <AddOrders
                open={editModalOpen}
                editOrderId={order?.id ?? null}
                onClose={() => setEditModalOpen(false)}
                onOrderCreated={() => {
                    setEditModalOpen(false);
                    fetchOrder();
                    onStatusChanged?.();
                }}
            />

            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, mb: 1 }}>Usunąć zamówienie?</Typography>
                    <Typography sx={{ color: 'text.secondary', mb: 3 }}>
                        {order?.kontrahenci?.name} — tej operacji nie można cofnąć. Wszystkie pozycje tego zamówienia zostaną usunięte razem z nim.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                            disabled={deleting}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                        >
                            {deleting ? 'Usuwanie...' : 'Usuń'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}

export default OrdersDetails;